"use client";

import { forwardRef, useState } from "react";
import { Select } from "@/components/ui/select";
import { Icon, type IconName } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { FOCUS_RING } from "./icon-button";
import { PRESET_TYPE_CATEGORIES, type PresetTypeSlug } from "../lib/categories";

const PRESET_TYPE_OPTIONS: {
  value: PresetTypeSlug;
  label: string;
  icon: IconName;
}[] = PRESET_TYPE_CATEGORIES.map((cat) => ({
  value: cat.slug as PresetTypeSlug,
  label: cat.label,
  icon: cat.icon,
}));

interface PresetTypeInputProps {
  value: PresetTypeSlug;
  onChange: (v: PresetTypeSlug) => void;
  className?: string;
  id?: string;
  name?: string;
  onBlur?: () => void;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export const PresetTypeInput = forwardRef<
  HTMLButtonElement,
  PresetTypeInputProps
>(function PresetTypeInput(
  { value, onChange, className, onBlur, ...rest },
  ref,
) {
  const [open, setOpen] = useState(false);
  const current = PRESET_TYPE_OPTIONS.find((o) => o.value === value);

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger aria-label={`Preset type: ${current?.label ?? value}`}>
        {current && (
          <Icon
            name={current.icon}
            size={20}
            aria-hidden="true"
            className="text-heading opacity-60 shrink-0"
          />
        )}
        <span className="truncate text-left">{current?.label ?? value}</span>
        <Select.Icon className="ml-auto" />
      </Select.Trigger>
      <Select.Content>
        <Select.List>
          {PRESET_TYPE_OPTIONS.map((opt) => (
            <Select.Item
              key={opt.value}
              selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <Icon
                name={opt.icon}
                size={18}
                aria-hidden="true"
                className="text-heading opacity-60 shrink-0"
              />
              <span className="text-sm text-heading text-left truncate flex-1">
                {opt.label}
              </span>
              {opt.value === value && (
                <Icon
                  name="IconSubscriptionTick1"
                  size={16}
                  aria-hidden="true"
                  className="text-heading shrink-0"
                />
              )}
            </Select.Item>
          ))}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
});
